import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserUiPreference } from '@/hooks/useUserUiPreference';

/**
 * Which conversations screen the operator is looking at.
 * 'v1' = the classic screen (src/pages/Conversations.tsx)
 * 'v2' = the new screen  (src/pages/ConversationsV2.tsx)
 */
export type ConversationsVersion = 'v1' | 'v2';

export const CONVERSATIONS_VERSION_KEY = 'okos.conversations.version';

/**
 * Calls a database function that the generated types do not know about.
 *
 * `set_user_ui_preference` and `record_conversations_version_switch` are both absent from
 * src/integrations/supabase/types.ts, so the typed client refuses them at compile time. This is the
 * narrowest way through: it types the ONE call — a name, a plain object of arguments, and back the
 * only field either caller reads. The previous `supabase as any` turned checking off for the whole
 * client expression, which also silenced `.rpc` being misspelled or the result being misused.
 */
type UntypedRpc = (name: string, args: Record<string, unknown>) => PromiseLike<{ error: unknown }>;

function callRpc(name: string, args: Record<string, unknown>) {
  return (supabase as unknown as { rpc: UntypedRpc }).rpc(name, args);
}

/** Key under public.users.ui_preferences (see
 * supabase/migrations/20260816090000_add_user_ui_preferences.sql) that stores this preference
 * per PERSON rather than per browser. */
export const CONVERSATIONS_VERSION_PREF_KEY = 'conversations_version';

/**
 * V2 is the default since 2026-09-07. The operator who orchestrates this platform decided it in so
 * many words: the version 2 of conversations should replace version 1. Before that day this
 * constant was 'v1' and the switch was opt-in.
 *
 * WHAT THE FLIP DID AND DID NOT SETTLE, because the difference is what someone reverting this needs:
 *
 *   · The amber preview strip is gone, not inert. Its whole subject was "this screen is not the
 *     default one", and that sentence stopped being true here.
 *   · The toggle stays, in both directions. That is the reversion: anyone who wants the classic
 *     screen picks it and it follows them to their next device, and this constant can go back to
 *     'v1' with the same one-line edit that brought it here.
 *   · Five rows of docs/mods/conversations-v2/V1_V2_PARITY_CHECKLIST.md are still unticked and were
 *     unticked on the day of the flip. Every one of them needs a WRITE proven against a real
 *     project — a message sent, an escalation resolved — and no identity that can write existed
 *     yet. They were not waived: they moved from "before the flip" to "still owed".
 *
 * The measurement keeps running either way:
 * supabase/migrations/20260816090100_add_conversations_version_switch_tracking.sql records every
 * switch and its direction, so people leaving the new screen is a number and not an impression.
 */
export const DEFAULT_CONVERSATIONS_VERSION: ConversationsVersion = 'v2';

/**
 * The URL wins over the stored preference while it is present (`?v=1` / `?v=2`), so a link can
 * pin a specific version without touching what the operator normally sees.
 */
export const VERSION_SEARCH_PARAM = 'v';

function isVersion(value: unknown): value is ConversationsVersion {
  return value === 'v1' || value === 'v2';
}

/** Accepts both `?v=2` and `?v=v2`. */
function parseVersionParam(raw: string | null): ConversationsVersion | null {
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase();
  if (normalized === '2' || normalized === 'v2') return 'v2';
  if (normalized === '1' || normalized === 'v1') return 'v1';
  return null;
}

/**
 * Real in-memory fallback for when `localStorage.setItem` throws (e.g. Safari private-mode
 * quota, which — unlike most storage-disabled cases — lets reads through but blocks writes).
 * Without this, `writeStored`'s catch block used to CLAIM "the in-memory listeners still flip
 * the screen for this session" while there was no in-memory state at all: `readStored()` only
 * ever read `localStorage`, so a blocked write meant the next render kept showing the OLD
 * version despite `emit()` having fired. This variable is what makes that promise true.
 */
let memoryFallbackVersion: ConversationsVersion | null = null;

function readStored(): ConversationsVersion {
  try {
    const stored = window.localStorage.getItem(CONVERSATIONS_VERSION_KEY);
    if (isVersion(stored)) return stored;
  } catch {
    // Private mode / storage disabled for reads too — fall through to the in-memory fallback.
  }
  return memoryFallbackVersion ?? DEFAULT_CONVERSATIONS_VERSION;
}

/**
 * Puts the stored preference back to the default. Called when the new screen crashes: without it,
 * the preference outlives the crash and a reload lands on the same broken screen.
 *
 * Also writes the default back to the per-person preference, best-effort: without this, the NEXT
 * device or browser this person opens would still be handed the version that just crashed. Never
 * blocks the recovery screen on this — it's fire-and-forget, same as every other write-through in
 * this module.
 */
export function resetConversationsVersion() {
  try {
    window.localStorage.removeItem(CONVERSATIONS_VERSION_KEY);
  } catch {
    // Nothing to clear.
  }
  emit();
  versionEnteredAt = Date.now();
  enteredAtReliable = true;
  writeEnteredAtStorage(DEFAULT_CONVERSATIONS_VERSION, versionEnteredAt);

  void callRpc('set_user_ui_preference', {
    key: CONVERSATIONS_VERSION_PREF_KEY,
    value: DEFAULT_CONVERSATIONS_VERSION,
  })
    .then(({ error }: { error: unknown }) => {
      if (error && import.meta.env.DEV) {
        console.warn('[conversations] failed to reset the stored version preference:', error);
      }
    });
}

/**
 * Same-tab writes do not fire the `storage` event, so the store keeps its own listener set.
 *
 * Deliberately NOT listening to `storage`: syncing across tabs would swap the screen out from
 * under someone who is mid-reply in another tab. A tab keeps the version it started with until
 * its own toggle is clicked; the stored value only decides what the NEXT tab opens with.
 */
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getServerSnapshot(): ConversationsVersion {
  return DEFAULT_CONVERSATIONS_VERSION;
}

function writeStored(version: ConversationsVersion) {
  memoryFallbackVersion = version;
  try {
    window.localStorage.setItem(CONVERSATIONS_VERSION_KEY, version);
  } catch {
    // Storage write blocked — memoryFallbackVersion above (set unconditionally, just before this
    // try) is what actually flips the screen for the rest of this session now.
  }
  emit();
}

/**
 * Persisted so `versionEnteredAt` survives a page reload within the same tab. Without this, a
 * plain module-level variable reset to Date.now() on every load, and "seconds on the previous
 * screen" silently became "seconds since the last reload" — biasing every measured duration low,
 * worse the more the operator reloads. sessionStorage (not localStorage) on purpose: this clock
 * belongs to the tab, same as versionEnteredAt itself, and should not leak across tabs or survive
 * closing this one.
 */
const VERSION_ENTERED_AT_STORAGE_KEY = 'okos.conversations.versionEnteredAt';

interface EnteredAtRecord {
  version: ConversationsVersion;
  enteredAt: number;
}

/** Only trusted when it names the version we're actually about to leave — a stale entry for a
 * different version (e.g. the preference reconciled to something else) tells us nothing about
 * how long we've been on THIS one. */
function readEnteredAtStorage(forVersion: ConversationsVersion): number | null {
  try {
    const raw = window.sessionStorage.getItem(VERSION_ENTERED_AT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EnteredAtRecord>;
    if (parsed.version !== forVersion) return null;
    if (typeof parsed.enteredAt !== 'number' || !Number.isFinite(parsed.enteredAt)) return null;
    return parsed.enteredAt;
  } catch {
    return null;
  }
}

function writeEnteredAtStorage(version: ConversationsVersion, enteredAt: number) {
  try {
    const record: EnteredAtRecord = { version, enteredAt };
    window.sessionStorage.setItem(VERSION_ENTERED_AT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Best-effort — recordVersionSwitch below falls back to entry_time_reliable=false when this
    // doesn't survive to be read back.
  }
}

/**
 * When the version currently shown started being shown — module-level (not per-hook-instance)
 * because several components call useConversationsVersion() at once and the "how long has the
 * operator been on this screen" clock belongs to the tab, not to any one of them.
 *
 * `enteredAtReliable` is false only for the true first switch in a fresh tab (no sessionStorage
 * entry to restore) — the one case the old code approximated with Date.now() while claiming
 * (falsely — see the SQL migration's column comment) that it sent NULL for it. Now it sends a
 * real number plus an honest reliability flag instead.
 */
let versionEnteredAt = typeof window !== 'undefined' ? Date.now() : 0;
let enteredAtReliable = false;
if (typeof window !== 'undefined') {
  const restored = readEnteredAtStorage(readStored());
  if (restored !== null) {
    versionEnteredAt = restored;
    enteredAtReliable = true;
  } else {
    writeEnteredAtStorage(readStored(), versionEnteredAt);
  }
}

/**
 * Records one operator-initiated switch for measurement (see
 * supabase/migrations/20260816090100_add_conversations_version_switch_tracking.sql and
 * 20260816090200_fix_conversations_version_measurement_query.sql). Fire-and-forget: never
 * awaited by the caller, never blocks the screen switch, and the only failure handling is a
 * DEV-only console warning — a failed measurement write is not something the operator needs to
 * know about or be blocked by.
 */
function recordVersionSwitch(from: ConversationsVersion, to: ConversationsVersion) {
  const secondsOnPrevious = Math.max(0, Math.round((Date.now() - versionEnteredAt) / 100) / 10);
  const reliable = enteredAtReliable;
  versionEnteredAt = Date.now();
  enteredAtReliable = true;
  writeEnteredAtStorage(to, versionEnteredAt);

  void callRpc('record_conversations_version_switch', {
    to_version: to,
    from_version: from,
    seconds_on_previous: secondsOnPrevious,
    entry_time_reliable: reliable,
  })
    .then(({ error }: { error: unknown }) => {
      if (error && import.meta.env.DEV) {
        console.warn('[conversations] failed to record the version switch:', error);
      }
    });
}

/**
 * Reads and writes the conversations screen preference.
 *
 * Resolution order: `?v=` in the URL (if present and valid) → local cache → per-person preference
 * (once it loads) → 'v1'.
 *
 * Zero flicker: `localStorage` is read synchronously on the very first render (via
 * `useUserUiPreference`'s `initialData`), so the first paint never shows the wrong screen and
 * swaps. The per-person preference then loads in the background and, if it differs from the local
 * cache (e.g. this is a new browser, or another device set something else), reconciles into it
 * ONCE — see the effect below. After that first settle, the value only changes when this hook's
 * own `setVersion` is called: window-focus/reconnect refetches are disabled in
 * useUserUiPreference specifically so the operator never sees the screen change on its own while
 * they are using it.
 *
 * Works without the database: `setVersion` always writes the local cache first — that part never
 * depends on the network — and the write-through to the per-person preference is best-effort;
 * useUserUiPreference already rolls its optimistic cache back on failure, but the version this
 * hook returns to the caller is driven by the local cache, so a failed write-through never blocks
 * or reverts the switch the operator just made in this tab.
 *
 * Both directions are a plain re-render: V1 is eagerly imported, so going back is immediate.
 */
export function useConversationsVersion() {
  const [searchParams, setSearchParams] = useSearchParams();
  const localCached = useSyncExternalStore(subscribe, readStored, getServerSnapshot);

  const pref = useUserUiPreference<ConversationsVersion>(
    CONVERSATIONS_VERSION_PREF_KEY,
    localCached,
    { initialData: readStored, isValid: isVersion },
  );

  // Reconcile once: when the per-person preference resolves to something other than what the
  // local cache currently shows, adopt it. This only fires when pref.value actually changes
  // (React's dependency array), so it is a one-time settle after the background fetch — not a
  // recurring sync that could yank the screen mid-session (that path is already closed off by
  // disabling refetchOnWindowFocus/refetchOnReconnect in useUserUiPreference).
  useEffect(() => {
    // Guard against an empty/unknown value overwriting a good local choice. useUserUiPreference is
    // handed `localCached` as its fallback so it should never hand back anything else, but this
    // effect is the one place that can WIPE the operator's choice, and a wipe is silent: they land
    // on the classic screen with no idea why. Cheaper to check than to debug.
    if (isVersion(pref.value) && pref.value !== readStored()) {
      writeStored(pref.value);
      versionEnteredAt = Date.now();
      enteredAtReliable = true;
      writeEnteredAtStorage(pref.value, versionEnteredAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pref.value]);

  const pinned = parseVersionParam(searchParams.get(VERSION_SEARCH_PARAM));
  const version: ConversationsVersion = pinned ?? localCached;

  const setVersion = useCallback((next: ConversationsVersion) => {
    const previous = readStored();
    // 1. Instant, same-tab, works without the database: the local cache changes immediately,
    //    regardless of what the network does next.
    writeStored(next);
    // 2. Best-effort write-through to the per-person preference, so the NEXT device/browser this
    //    person opens sees it too. If it fails (offline, expired session), this tab already
    //    switched in step 1 and nothing here surfaces an error to the operator.
    pref.setValue(next);
    // 3. Measurement — fire-and-forget, never blocks steps 1-2.
    if (previous !== next) recordVersionSwitch(previous, next);

    setSearchParams(
      current => {
        const params = new URLSearchParams(current);
        // A pinned `?v=` would otherwise win over the click that just happened.
        if (params.has(VERSION_SEARCH_PARAM)) params.set(VERSION_SEARCH_PARAM, next);
        // `id` is deliberately kept: both screens group rows by customer and both resolve a
        // conversation id to its customer (V1 falls back to getCustomerIdForConversation when the
        // id is not on the loaded page), so the operator lands on the same conversation on the
        // other side instead of back at the top of the list.
        return params;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pref.setValue, setSearchParams]);

  return { version, setVersion, isPinnedByUrl: pinned !== null, isSyncing: pref.isSaving };
}
